import { FC, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { get } from 'lodash';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@material-ui/core';
import { injectIntl, IntlShape } from 'react-intl';
import moment from 'moment';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import messages from 'containers/ExpertAssignedMissions/messages';
import { translate } from 'containers/services/translate';
import {
  Container,
  Header,
  Title,
  StyledTableHead,
  StyledTablePagination,
  StyledTableRow,
} from 'containers/ExpertAssignedMissions/style';
import { makeSelectKeycloakToken } from 'containers/AppContainer/selectors';
import { requestExpertExpertiseMissions } from 'containers/ExpertAssignedMissions/actions';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import ActionDropDown from 'components/ActionDropDown/index';
import {
  extractStatusExpert,
  renderNatureMission,
} from 'containers/ExpertAssignedMissions/utils';
import { getValueByCode as getValueByCodeSagis } from 'containers/CourtManagerLegalProceedings/Provision/utils';
import { getValueByCode } from 'containers/common/LegalProceedings/JudgementAdd/Recap/utils';
import { LISTINGS } from 'containers/data-structure';
import { createStructuredSelector } from 'reselect';
import { makeSelectedMissions } from 'containers/ExpertHomePage/selectors';
import {
  requestFindMissions,
  resetFoundMissionsStore,
} from 'containers/ExpertHomePage/actions';
import { resetStoreClaim } from 'containers/RecapManagerPage/actions';
import { SearchQuery } from 'containers/ExpertHomePage/typings';
import { resetStoreExpertise } from 'containers/ExpertAssignedMissions/actions';
import { resetSearchClaimsStore } from 'containers/IdentificationPage/actions';
import { resetStoreDetailsExpertise } from 'containers/ExpertExpertise/actions';
import SearchBarExpert from 'containers/ExpertHomePage/SearchBarExpert';
import {
  STATUS_REJECTED_MISSION_ID,
  STATUS_ACHIEVED_MISSION_ID,
  STATUS_PV_DEFICIENCY_MISSION_ID,
} from 'containers/ExpertAssignedMissions/constants';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';

export interface ExpertCompletedMissionProps {
  intl: IntlShape;
  requestExpertExpertiseMissions: Function;
  location: { pathname: string };
  requestFindMissions: Function;
  resetFoundMissionsStore: Function;
  foundMissions: any;
  resetSearchClaimsStore: Function;
  resetStoreClaim: Function;
  resetStoreExpertise: Function;
  resetStoreDetailsExpertise: Function;
}

const ExpertCompletedMission: FC<ExpertCompletedMissionProps> = props => {
  const { requestExpertExpertiseMissions } = props;

  useEffect(() => {
    props.resetSearchClaimsStore();
    props.resetStoreClaim();
    props.resetStoreExpertise();
    props.resetStoreDetailsExpertise();
  }, []);

  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    cin: null,
    claimNumber: null,
    reference: null,
    status: [
      STATUS_REJECTED_MISSION_ID,
      STATUS_ACHIEVED_MISSION_ID,
      STATUS_PV_DEFICIENCY_MISSION_ID,
    ],
  });
  const listings = useSelector((state: any) =>
    get(state.toJS(), ['appContainer', 'listings'], []),
  );
  const token = useSelector(makeSelectKeycloakToken());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isSearch, setIsSearch] = useState(false);
  /* istanbul ignore next */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const tableHeaderTitles = featureService.hasFeature(FEATURES.TRANSACTION)
    ? [
        translate(messages.claimNumber, props),
        translate(messages.missionNumber, props),
        translate(messages.missionDate, props),
        translate(messages.concernedParty, props),
        translate(messages.city, props),
        translate(messages.lawyerAdvice, props),
        translate(messages.missionStatus, props),
        translate(messages.nature, props),
        '',
      ]
    : [
        translate(messages.claimNumber, props),
        translate(messages.missionNumber, props),
        translate(messages.missionDate, props),
        translate(messages.concernedParty, props),
        translate(messages.city, props),
        translate(messages.lawyerAdvice, props),
        translate(messages.missionStatus, props),
        '',
      ];
  const missions = useSelector((state: any) =>
    get(
      state.toJS(),
      ['expertExpertiseMissions', 'expertExpertiseMissions'],
      [],
    ),
  );
  useEffect(() => {
    requestExpertExpertiseMissions([
      STATUS_REJECTED_MISSION_ID,
      STATUS_ACHIEVED_MISSION_ID,
      STATUS_PV_DEFICIENCY_MISSION_ID,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  /* istanbul ignore next */
  const submitSearch = () => {
    setIsSearch(true);
    props.resetFoundMissionsStore();
    props.requestFindMissions({ ...searchQuery, isLawyer: true });
  };

  /* istanbul ignore next */
  const handlerRefresh = () => {
    setIsSearch(false);
    props.resetFoundMissionsStore();
  };

  const resolveMissions = () => {
    if (isSearch) return get(props.foundMissions, 'missions', []);
    return missions;
  };

  const missionRows: any[] = resolveMissions().map((mission: any) => ({
    missionNumber: mission?.reference,
    assignedExperts: mission?.assignedExperts,
    assignedExpert: mission?.assignedExpert,
    courtFileNumber: mission?.courtFileNumber,
    type: mission?.type,
    missionDate: mission?.missionDate,
    claimNumber: mission?.claim?.claimNumber,
    city: getValueByCode(
      mission?.city,
      get(listings, LISTINGS.JURISDICTION, []),
    ),
    address: mission?.address,
    document: mission?.document,
    lossHour: mission?.claim?.lossHour,
    procedureNatureId: mission?.procedureNatureId,
    stepId: mission?.step,
    legalExpert: mission?.legalExpert,
    vehicles: mission?.vehicles,
    lawyer: getValueByCodeSagis(mission?.lawyer, get(listings, 'intervenants')),
    victim:
      get(mission?.victims, '[0].firstName', ' ') +
      ' ' +
      get(mission?.victims, '[0].lastName', ' '),
    vehicle: get(mission?.vehicles, '[0].registrationNumber', ' '),
    claimId: mission?.claim?.id,
    id: mission?.id,
    nature: mission?.nature,
    victims: mission?.victims,
  }));

  return (
    <>
      <Container>
        <Header>
          <Title>{translate(messages.processedFiles, props)}</Title>
          <div className="containerSearch">
            <SearchBarExpert
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handlerSearch={submitSearch}
              handlerRefresh={handlerRefresh}
            />
          </div>
        </Header>
        <TableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                {tableHeaderTitles.map(title => (
                  <TableCell key={title}>{title}</TableCell>
                ))}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {missionRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any) => {
                  return (
                    <StyledTableRow
                      data-testid="mission-row"
                      key={`${row.missionNumber}${row.victim}`}
                      onClick={() => {
                        pushTo(
                          `${PAGES.EXPERT_ASSIGNED_MISSION_SHOW}/${row.id}`,
                        )();
                      }}
                    >
                      <TableCell>{row.claimNumber}</TableCell>
                      <TableCell>{row.missionNumber}</TableCell>
                      <TableCell>
                        {moment(row.missionDate).format('DD/MM/yyyy')}
                      </TableCell>

                      <TableCell>
                        {row.victim ? row.victim : row.vehicle}
                      </TableCell>
                      <TableCell>{row.city}</TableCell>
                      <TableCell>{row.lawyer}</TableCell>
                      <TableCell>
                        {getValueByCode(
                          extractStatusExpert(
                            row,
                            currentIntervenantStorage.get()?.sagis_code,
                          ),
                          get(listings, 'missionStatus', []),
                        )}
                      </TableCell>
                      {featureService.hasFeature(FEATURES.TRANSACTION) && (
                        <TableCell>
                          {translate(
                            renderNatureMission(get(row, 'nature')),
                            props,
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <ActionDropDown
                          label={translate(messages.action, props)}
                          items={
                            extractStatusExpert(
                              row,
                              currentIntervenantStorage.get()?.sagis_code,
                            ) === STATUS_PV_DEFICIENCY_MISSION_ID ||
                            extractStatusExpert(
                              row,
                              currentIntervenantStorage.get()?.sagis_code,
                            ) === STATUS_ACHIEVED_MISSION_ID
                              ? [
                                  {
                                    title: translate(messages.consult, props),
                                    onClick: pushTo(
                                      `${PAGES.EXPERT_ASSIGNED_MISSION_SHOW}/${row.id}?tab=0`,
                                    ),
                                  },
                                ]
                              : [
                                  {
                                    title: translate(messages.consult, props),
                                    onClick: pushTo(
                                      `${PAGES.EXPERT_ASSIGNED_MISSION_SHOW}/${row.id}?tab=0`,
                                    ),
                                  },
                                  {
                                    title: translate(messages.treat, props),
                                    onClick: pushTo(
                                      `${PAGES.EXPERT_ASSIGNED_MISSION_SHOW}/${row.id}?tab=1`,
                                    ),
                                  },
                                ]
                          }
                        />
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <StyledTablePagination
          rowsPerPageOptions={[5, 10, 25]}
          count={missionRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage={translate(messages.rowsPerPage, props)}
        />
      </Container>
    </>
  );
};

const mapDispatchToProps = {
  requestExpertExpertiseMissions,
  requestFindMissions,
  resetFoundMissionsStore,
  resetSearchClaimsStore,
  resetStoreClaim,
  resetStoreExpertise,
  resetStoreDetailsExpertise,
};

const mapStateToProps = createStructuredSelector({
  foundMissions: makeSelectedMissions(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(ExpertCompletedMission));
